/**
 * Resolve one translated field out of a PIM translations array (`AttributeDefinitionTranslation`,
 * `ProductFamilyTranslation`, `MeasurementFamilyTranslation`, …) for display in a table/chip.
 *
 * Catalog content languages (tenant-configured via `/catalog/languages`) are independent of the
 * merchant-portal UI language, so this is a best-effort lookup, not a strict i18n resolution.
 *
 * **Resolution order (adr-tenant-005, plan-tenant-004 Wave 2):** the tenant's content-**default**
 * locale first (`defaultLocale`, sourced from the catalog-language registry — most likely to carry
 * a real translation, since it can never be disabled or removed), then an exact match on
 * `currentLocale` (the merchant-portal UI's own language), then the first stored entry, else the
 * caller's `fallback` (typically the entity's stable `code`). `defaultLocale` is optional and
 * backward-compatible: call sites that don't have the registry in scope simply omit it and keep the
 * pre-existing `currentLocale`-first behaviour.
 */
export function resolveLocalizedText<T extends { locale: string }>(
  translations: T[] | undefined,
  field: keyof T,
  currentLocale: string,
  fallback: string,
  defaultLocale?: string,
): string {
  if (!translations || translations.length === 0) return fallback;
  const byDefault = defaultLocale ? translations.find((entry) => entry.locale === defaultLocale) : undefined;
  const byCurrent = translations.find((entry) => entry.locale === currentLocale);
  const chosen = byDefault ?? byCurrent ?? translations[0];
  const value = chosen[field];
  return typeof value === 'string' && value.trim() !== '' ? value : fallback;
}
